import {
  type FilaDeComparacion,
  formatearFecha,
  formatearRevision,
  NOMBRE_ESTADO,
  type TramoDeDiff,
} from "@gestiondoc/shared";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { type ReactNode, useState } from "react";
import { useTRPC } from "../api.ts";
import { Encabezado } from "../componentes/Encabezado.tsx";
import { Boton, BotonEnlace, EstadoVacio, Pildora, Tarjeta } from "../componentes/ui.tsx";

/**
 * Fase 4 · pantalla 07, "Comparar revisiones": qué cambió entre dos revisiones cualesquiera, a
 * dos columnas alineadas párrafo a párrafo. Izquierda la anterior, derecha la nueva; lo igual va
 * en blanco, lo nuevo en verde, lo que cambió en ámbar (con las palabras), lo eliminado en rojo
 * tachado. Si alguna de las dos es un archivo, se comparan solo sus datos (el diff de PDFs no
 * entra en el MVP). "Exportar comparación PDF" imprime esta misma vista (Guardar como PDF).
 */

const ESTILO_FILA: Record<FilaDeComparacion["tipo"], { izquierda: string; derecha: string }> = {
  igual: { izquierda: "", derecha: "" },
  modificado: {
    izquierda: "bg-[#fbf1de] border-l-[3px] border-[#b7791f]",
    derecha: "bg-[#fbf1de] border-l-[3px] border-[#b7791f]",
  },
  agregado: { izquierda: "", derecha: "bg-[#e3f4ec] border-l-[3px] border-[#1b8a5a]" },
  eliminado: { izquierda: "bg-[#fbe9e7] border-l-[3px] border-[#b42318]", derecha: "" },
  reemplazo: {
    izquierda: "bg-[#fbe9e7] border-l-[3px] border-[#b42318]",
    derecha: "bg-[#e3f4ec] border-l-[3px] border-[#1b8a5a]",
  },
  movido: { izquierda: "", derecha: "bg-[#eef2f8] border-l-[3px] border-marino" },
};

export function PaginaComparar() {
  const { codigo } = useParams({ from: "/app/documentos/$codigo/comparar" });
  const busqueda = useSearch({ from: "/app/documentos/$codigo/comparar" });
  const trpc = useTRPC();
  const navigate = useNavigate();
  const detalle = useQuery(trpc.documentos.detalle.queryOptions({ codigo }));
  const [ocultarIguales, setOcultarIguales] = useState(false);

  // Sin números en la URL: la principal contra la anterior que se ve (en el mockup, 03 → 04).
  const numeros = detalle.data?.revisiones.map((r) => r.numero).sort((x, y) => x - y) ?? [];
  const principal = detalle.data?.revisiones.find((r) => r.id === detalle.data?.principalId);
  const b = busqueda.b ?? principal?.numero ?? numeros.at(-1) ?? 1;
  const a = busqueda.a ?? [...numeros].reverse().find((n) => n < b) ?? b;

  const comparacion = useQuery({
    ...trpc.contenido.comparar.queryOptions({ codigo, a, b }),
    enabled: !!detalle.data,
    placeholderData: (previa) => previa,
  });

  if (detalle.isPending || (comparacion.isPending && !comparacion.data)) {
    return <EstadoVacio>Cargando…</EstadoVacio>;
  }
  if (detalle.isError) return <EstadoVacio>{detalle.error.message}</EstadoVacio>;
  if (comparacion.isError) return <EstadoVacio>{comparacion.error.message}</EstadoVacio>;
  const c = comparacion.data;
  if (!c) return null;
  const elegir = (cambio: { a?: number; b?: number }) =>
    navigate({
      to: "/documentos/$codigo/comparar",
      params: { codigo },
      search: { a: cambio.a ?? a, b: cambio.b ?? b },
    });
  const etiqueta = (n: number) => {
    const r = c.revisiones.find((x) => x.numero === n);
    return r ? `Rev. ${formatearRevision(n)} · ${formatearFecha(r.fecha)}` : `Rev. ${n}`;
  };

  return (
    <div className="max-w-[1180px] pb-10">
      <Encabezado
        overline={`${c.documento.proceso} › Documentos › ${c.documento.codigo} › Ver cambios`}
        titulo={`Qué cambió entre la Rev. ${formatearRevision(c.antes.numero)} y la Rev. ${formatearRevision(c.despues.numero)}`}
        acciones={
          <span className="flex gap-2 print:hidden">
            <BotonEnlace to="/documentos/$codigo" params={{ codigo }}>
              Volver al documento
            </BotonEnlace>
            <Boton onClick={() => window.print()}>Exportar comparación PDF</Boton>
          </span>
        }
      />

      <Tarjeta className="mb-4" etiqueta="Cómo leer esta pantalla">
        <p className="text-[12.5px] text-texto-2">
          Izquierda: la versión anterior (Rev. {formatearRevision(c.antes.numero)}). Derecha: la
          versión nueva (Rev. {formatearRevision(c.despues.numero)}). Los párrafos iguales van en
          blanco; los que cambiaron se pintan.
        </p>
        <div className="mt-2 flex flex-wrap gap-4 text-[12px]">
          <Muestra clase="bg-[#e3f4ec] border-[#1b8a5a]">texto nuevo</Muestra>
          <Muestra clase="bg-[#fbf1de] border-[#b7791f]">texto que cambió</Muestra>
          <Muestra clase="bg-[#fbe9e7] border-[#b42318]">
            <span className="line-through">texto eliminado</span>
          </Muestra>
        </div>
      </Tarjeta>

      <div className="mb-4 flex flex-wrap items-center gap-x-4 gap-y-2">
        <span className="font-mono text-[13px] font-semibold text-marino">
          {c.documento.codigo}
        </span>
        <span className="text-[13px]">{c.documento.nombre}</span>
        <Pildora tono="bg-marino-claro text-marino">
          Rev. {formatearRevision(c.antes.numero)} → Rev. {formatearRevision(c.despues.numero)}
        </Pildora>
        {c.diff && (
          <span className="flex flex-wrap gap-3 text-[12.5px] font-semibold">
            <span className="text-[#1b8a5a]">
              {c.diff.resumen.agregados} agregado{c.diff.resumen.agregados === 1 ? "" : "s"}
            </span>
            <span className="text-[#b7791f]">
              {c.diff.resumen.modificados} modificado{c.diff.resumen.modificados === 1 ? "" : "s"}
            </span>
            <span className="text-[#b42318]">
              {c.diff.resumen.eliminados} eliminado{c.diff.resumen.eliminados === 1 ? "" : "s"}
            </span>
            {c.diff.resumen.movidos > 0 && (
              <span className="text-marino">
                {c.diff.resumen.movidos} movido{c.diff.resumen.movidos === 1 ? "" : "s"}
              </span>
            )}
          </span>
        )}
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-3 text-[12.5px] print:hidden">
        <label className="flex items-center gap-2">
          Comparar
          <select
            value={a}
            onChange={(e) => elegir({ a: Number(e.target.value) })}
            className="rounded-md border border-borde bg-white px-2 py-1.5"
          >
            {c.revisiones.map((r) => (
              <option key={r.numero} value={r.numero}>
                {etiqueta(r.numero)}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-2">
          con
          <select
            value={b}
            onChange={(e) => elegir({ b: Number(e.target.value) })}
            className="rounded-md border border-borde bg-white px-2 py-1.5"
          >
            {c.revisiones.map((r) => (
              <option key={r.numero} value={r.numero}>
                {etiqueta(r.numero)}
              </option>
            ))}
          </select>
        </label>
        {c.diff && (
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={ocultarIguales}
              onChange={(e) => setOcultarIguales(e.target.checked)}
            />
            Ocultar párrafos sin cambios
          </label>
        )}
      </div>

      {c.diff ? (
        <TablaDeCambios
          filas={c.diff.filas}
          ocultarIguales={ocultarIguales}
          encabezados={[
            `Rev. ${formatearRevision(c.antes.numero)} · ${NOMBRE_ESTADO[c.antes.estado]}`,
            `Rev. ${formatearRevision(c.despues.numero)} · ${NOMBRE_ESTADO[c.despues.estado]}`,
          ]}
        />
      ) : (
        <DatosComparados antes={c.antes} despues={c.despues} />
      )}
    </div>
  );
}

function Muestra({ clase, children }: { clase: string; children: ReactNode }) {
  return (
    <span className="flex items-center gap-1.5">
      <span className={`inline-block h-3.5 w-5 border-l-[3px] ${clase}`} />
      {children}
    </span>
  );
}

function TablaDeCambios({
  filas,
  ocultarIguales,
  encabezados,
}: {
  filas: FilaDeComparacion[];
  ocultarIguales: boolean;
  encabezados: [string, string];
}) {
  // Un párrafo vacío que sigue vacío (la sección opcional sin llenar) no dice nada.
  const visibles = filas.filter(
    (f) => f.tipo !== "igual" || (!ocultarIguales && f.despues?.texto.trim()),
  );
  if (!visibles.length) {
    return <EstadoVacio>Las dos revisiones dicen lo mismo.</EstadoVacio>;
  }
  let seccionAnterior: string | null = null;
  return (
    <div className="overflow-hidden rounded-tarjeta border border-borde bg-white">
      <div className="grid grid-cols-2 border-b border-borde bg-fondo text-[11px] font-semibold tracking-[.08em] text-texto-3 uppercase">
        <div className="px-4 py-2">{encabezados[0]}</div>
        <div className="border-l border-borde px-4 py-2">{encabezados[1]}</div>
      </div>
      {visibles.map((f, i) => {
        const nuevaSeccion = f.seccion !== seccionAnterior;
        seccionAnterior = f.seccion;
        const estilo = ESTILO_FILA[f.tipo];
        return (
          // biome-ignore lint/suspicious/noArrayIndexKey: las filas de una comparación no se reordenan
          <div key={i} className="break-inside-avoid">
            {nuevaSeccion && (
              <div className="border-b border-borde/60 bg-white px-4 pt-3 pb-1 text-[11.5px] font-bold tracking-[.06em] text-marino uppercase">
                {f.seccion}
              </div>
            )}
            <div className="grid grid-cols-2 border-b border-borde/60 text-[13px] leading-relaxed">
              <div className={`px-4 py-2.5 ${estilo.izquierda}`}>
                <LadoIzquierdo fila={f} />
              </div>
              <div className={`border-l border-borde/60 px-4 py-2.5 ${estilo.derecha}`}>
                <LadoDerecho fila={f} />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Hueco({ children }: { children: ReactNode }) {
  return (
    <div className="rounded border border-dashed border-borde px-3 py-2 text-[12px] text-texto-3 italic">
      {children}
    </div>
  );
}

function Etiqueta({ children, color }: { children: ReactNode; color: string }) {
  return (
    <span
      className={`mr-1.5 rounded px-1.5 py-0.5 text-[10.5px] font-bold tracking-wide uppercase ${color}`}
    >
      {children}
    </span>
  );
}

function LadoIzquierdo({ fila: f }: { fila: FilaDeComparacion }) {
  if (!f.antes) return <Hueco>(no existía)</Hueco>;
  if (f.tipo === "eliminado" || f.tipo === "reemplazo") {
    return <span className="text-[#7a271a] line-through">{f.antes.texto}</span>;
  }
  if (f.tipo === "movido") {
    return (
      <span className="text-texto-3">
        <Etiqueta color="bg-marino-claro text-marino">estaba en otro lugar</Etiqueta>
        {f.antes.texto}
      </span>
    );
  }
  if (f.palabras) return <Tramos tramos={f.palabras} lado="antes" />;
  return <span>{f.antes.texto}</span>;
}

function LadoDerecho({ fila: f }: { fila: FilaDeComparacion }) {
  if (!f.despues) return <Hueco>(se eliminó)</Hueco>;
  if (f.tipo === "igual") return <span className="text-texto-3 italic">(sin cambios)</span>;
  if (f.tipo === "agregado" || f.tipo === "reemplazo") {
    return (
      <span>
        <Etiqueta color="bg-[#1b8a5a] text-white">nuevo</Etiqueta>
        {f.despues.texto}
      </span>
    );
  }
  if (f.tipo === "movido") {
    return (
      <span>
        <Etiqueta color="bg-marino text-white">movido</Etiqueta>
        {f.palabras ? <Tramos tramos={f.palabras} lado="despues" /> : f.despues.texto}
      </span>
    );
  }
  return f.palabras ? (
    <Tramos tramos={f.palabras} lado="despues" />
  ) : (
    <span>{f.despues.texto}</span>
  );
}

/** El diff de palabras de un lado: lo que se quitó (izquierda) o lo que se puso (derecha). */
function Tramos({ tramos, lado }: { tramos: TramoDeDiff[]; lado: "antes" | "despues" }) {
  return (
    <span>
      {tramos.map((t, i) => {
        if (t.tipo === "igual") {
          // biome-ignore lint/suspicious/noArrayIndexKey: tramos de un texto, en su orden
          return <span key={i}>{t.texto}</span>;
        }
        if (t.tipo === "eliminado" && lado === "antes") {
          return (
            // biome-ignore lint/suspicious/noArrayIndexKey: ídem
            <span key={i} className="rounded-sm bg-[#f6d3ce] text-[#7a271a] line-through">
              {t.texto}
            </span>
          );
        }
        if (t.tipo === "agregado" && lado === "despues") {
          return (
            // biome-ignore lint/suspicious/noArrayIndexKey: ídem
            <strong key={i} className="rounded-sm bg-[#c9ebd9] font-semibold text-[#14532d]">
              {t.texto}
            </strong>
          );
        }
        return null;
      })}
    </span>
  );
}

type Resumen = {
  numero: number;
  estado: keyof typeof NOMBRE_ESTADO;
  motivo: string;
  fecha: Date;
  origen: string;
  elaboro: string;
  redactada: boolean;
  archivo: { nombre: string; tamano: number } | null;
  hashContenido: string | null;
};

/** Revisiones con archivo (o sin contenido): lo que se puede comparar sin leer el archivo. */
function DatosComparados({ antes, despues }: { antes: Resumen; despues: Resumen }) {
  const filas: [string, (r: Resumen) => ReactNode][] = [
    ["Estado", (r) => NOMBRE_ESTADO[r.estado]],
    ["Fecha", (r) => formatearFecha(r.fecha)],
    ["Motivo del cambio", (r) => r.motivo],
    ["Elaboró", (r) => r.elaboro],
    [
      "Contenido",
      (r) =>
        r.redactada
          ? "Redactado en el editor"
          : r.archivo
            ? r.archivo.nombre
            : r.origen === "importado"
              ? "Importada del sistema anterior, sin archivo"
              : "Sin archivo",
    ],
    [
      "Huella (SHA-256)",
      (r) => <span className="font-mono text-[11px] break-all">{r.hashContenido ?? "—"}</span>,
    ],
  ];
  return (
    <Tarjeta
      titulo="Datos de cada revisión"
      ayuda="Alguna de las dos no está redactada en el editor: se comparan sus datos, no su texto (el diff de archivos no entra en esta versión)."
    >
      <div className="overflow-x-auto">
        <table className="w-full text-[13px]">
          <thead>
            <tr className="text-left text-[11px] font-semibold tracking-[.08em] text-texto-3 uppercase">
              <th className="px-3 py-2" />
              <th className="px-3 py-2">Rev. {formatearRevision(antes.numero)}</th>
              <th className="px-3 py-2">Rev. {formatearRevision(despues.numero)}</th>
            </tr>
          </thead>
          <tbody>
            {filas.map(([nombre, valor]) => {
              const x = valor(antes);
              const y = valor(despues);
              const cambia = typeof x === "string" && typeof y === "string" && x !== y;
              return (
                <tr key={nombre} className="border-t border-borde/60 align-top">
                  <td className="px-3 py-2 font-semibold text-texto-2">{nombre}</td>
                  <td className="px-3 py-2 whitespace-pre-line">{x}</td>
                  <td className={`px-3 py-2 whitespace-pre-line ${cambia ? "bg-[#fbf1de]" : ""}`}>
                    {y}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Tarjeta>
  );
}
